import Layout from "@/components/Layout";
import { useSettings } from "@/hooks/useSettings";
import { renderInlineLinks } from "@/lib/inline";
import SEO, { makeMetaDescription, SITE_ORIGIN, SITE_NAME } from "@/components/SEO";

// Detect a paragraph that is purely a block-level image markdown so we can
// render it as a centred figure rather than an inline `<img>` inside `<p>`.
// Matches `![alt](/path or http url)` with no other content on the line.
const BLOCK_IMG_ONLY_RE = /^\s*!\[([^\]]*)\]\((\/[^)\s]+|https?:\/\/[^)\s]+)\)\s*$/;

export default function AboutPage() {
  const { settings } = useSettings();
  const body = settings.about_body || "";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: `About — ${SITE_NAME}`,
    url: `${SITE_ORIGIN}/about`,
    description: makeMetaDescription(body),
    isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_ORIGIN },
  };

  return (
    <Layout>
      <SEO
        path="/about"
        title="About"
        description={makeMetaDescription(body)}
        structuredData={structuredData}
      />
      <section>
        <div className="max-w-[960px] mx-auto px-4">
          <div className="py-6 border-b border-black">
            <h1 className="serif text-4xl sm:text-5xl tracking-tight leading-tight">
              About What Do We Want?<br />
              <span className="italic" style={{ color: "#fc4707" }}>When Do We Want It?</span>
            </h1>
            <p className="text-neutral-700 mt-3 max-w-2xl">A publication exploring politics, media, and public opinion</p>
          </div>
        </div>
      </section>
      <article className="max-w-[960px] mx-auto px-4 pt-8 pb-12">
        <div className="prose max-w-none">
          {body.split(/\n\n+/).map((para, i) => {
            const imgOnly = para.match(BLOCK_IMG_ONLY_RE);
            if (imgOnly) {
              return (
                <figure key={i} className="my-8 flex justify-center">
                  <img
                    src={imgOnly[2]}
                    alt={imgOnly[1] || ""}
                    className="max-w-full h-auto"
                    loading="lazy"
                  />
                </figure>
              );
            }
            return (
              <p key={i} className="text-lg leading-relaxed mb-6 text-neutral-900">
                {renderInlineLinks(para)}
              </p>
            );
          })}
        </div>
      </article>
    </Layout>
  );
}
